import { Button } from '@breetometer/components';
import { DropdownWrapper } from 'BreetComponents';
import type { IDropDownChildArgs } from 'BreetHelpers';
import { type ReactNode, type RefObject, useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import { type ITooltip, normalizePlacement } from './Tooltip.helpers';

const CLOSE_DELAY_MS = 200;

const OPEN_DELAY_MS = 120;

const EXIT_MS = 120;

type ITooltipView = Pick<IDropDownChildArgs, 'controlRef' | 'dropdownRef' | 'setVisibility' | 'visible' | 'tipPlacement'> & {
	children?: ReactNode;
	tipElement?: ReactNode;
	unclipped?: boolean;
};

const TooltipView = ({
	controlRef,
	dropdownRef,
	setVisibility,
	visible,
	tipPlacement,
	children,
	tipElement,
	unclipped,
}: ITooltipView) => {
	const openTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
	const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
	const exitTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

	const [positioned, setPositioned] = useState(false);
	const [closing, setClosing] = useState(false);

	useEffect(
		() => () => {
			if (openTimer.current) clearTimeout(openTimer.current);
			if (closeTimer.current) clearTimeout(closeTimer.current);
			if (exitTimer.current) clearTimeout(exitTimer.current);
		},
		[]
	);

	useEffect(() => {
		if (!visible) {
			setPositioned(false);
			return;
		}

		let inner = 0;
		const outer = requestAnimationFrame(() => {
			inner = requestAnimationFrame(() => setPositioned(true));
		});

		return () => {
			cancelAnimationFrame(outer);
			cancelAnimationFrame(inner);
		};
	}, [visible]);

	const open = useCallback(() => {
		if (closeTimer.current) {
			clearTimeout(closeTimer.current);
			closeTimer.current = null;
		}
		if (exitTimer.current) {
			clearTimeout(exitTimer.current);
			exitTimer.current = null;
		}

		setClosing(false);

		if (visible || openTimer.current) return;

		openTimer.current = setTimeout(() => {
			openTimer.current = null;
			setVisibility(true);
		}, OPEN_DELAY_MS);
	}, [setVisibility, visible]);

	const scheduleClose = useCallback(() => {
		if (openTimer.current) {
			clearTimeout(openTimer.current);
			openTimer.current = null;
		}
		if (closeTimer.current) clearTimeout(closeTimer.current);

		closeTimer.current = setTimeout(() => {
			closeTimer.current = null;
			setClosing(true);
			exitTimer.current = setTimeout(() => {
				exitTimer.current = null;
				setVisibility(false);
				setClosing(false);
			}, EXIT_MS);
		}, CLOSE_DELAY_MS);
	}, [setVisibility]);

	const bubble = visible ? (
		<div
			role='tooltip'
			className={`tooltipelement bg-primary-400 border-primary-400 dropdownshadow${normalizePlacement(tipPlacement)}${
				positioned && !closing ? ' is-shown' : ''
			}${unclipped ? ' tooltipelement--top' : ''}`}
			ref={dropdownRef}
		>
			{tipElement}
		</div>
	) : null;

	return (
		<Button
			variant='native'
			className='tooltip button-cancel-transform'
			onMouseEnter={open}
			onMouseLeave={scheduleClose}
			type='button'
			ref={controlRef as RefObject<HTMLButtonElement>}
		>
			{children}
			{/* When unclipped, portal the tip to <body> so no overflow ancestor can clip it. */}
			{unclipped && bubble ? createPortal(bubble, document.body) : bubble}
		</Button>
	);
};

export const Tooltip = ({ children, tipElement, unclipped }: ITooltip) => (
	<DropdownWrapper strategy={unclipped ? 'fixed' : undefined}>
		{({ controlRef, dropdownRef, setVisibility, visible, tipPlacement }) => (
			<TooltipView
				controlRef={controlRef}
				dropdownRef={dropdownRef}
				setVisibility={setVisibility}
				visible={visible}
				tipPlacement={tipPlacement}
				tipElement={tipElement}
				unclipped={unclipped}
			>
				{children}
			</TooltipView>
		)}
	</DropdownWrapper>
);
